import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls, labelCls, AiAssistButton } from "./shared";

const EMPTY_ARTICLE = {
  slug: "", title: "", excerpt: "", body_markdown: "", hero_image_url: "",
  tags: [], author: "Sellvago Team", faqs: [],
  related_airlines: [], related_routes: [],
  seo_title: "", seo_description: "", published: true, sort_order: 0,
};

export function ArticlesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [tagsText, setTagsText] = useState("");

  const load = useCallback(() => {
    adminApi().get("/admin/articles").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (doc, newDoc = false) => {
    setForm(doc); setIsNew(newDoc);
    setTagsText((doc.tags || []).join(", "));
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const remove = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"?`)) return;
    await adminApi().delete(`/admin/articles/${slug}`);
    toast.success("Deleted"); load();
  };
  const save = async () => {
    try {
      const payload = { ...form, tags: tagsText.split(",").map(s => s.trim()).filter(Boolean) };
      if (isNew) await adminApi().post("/admin/articles", payload);
      else await adminApi().put(`/admin/articles/${form.slug}`, payload);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button data-testid="admin-add-article-btn" onClick={() => openEdit(EMPTY_ARTICLE, true)} className="flex items-center gap-2 bg-[#970C10] hover:bg-[#7A0A0D] text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors duration-200">
          <Plus className="w-4 h-4" /> New article
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Published</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.slug} className="border-t border-slate-100" data-testid={`admin-article-row-${it.slug}`}>
                <td className="px-4 py-3 font-mono text-xs"><a href={`/articles/${it.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#970C10] hover:underline">{it.slug}</a></td>
                <td className="px-4 py-3 font-semibold">{it.title}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${it.published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{it.published ? "Live" : "Draft"}</span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => openEdit(it, false)} className="text-slate-500 hover:text-[#970C10]"><Pencil className="w-4 h-4 inline" /></button>
                  <button onClick={() => remove(it.slug)} className="text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No articles yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-article-dialog">
          <DialogHeader><DialogTitle className="font-heading">{isNew ? "Create" : "Edit"} article</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Slug</label><input className={inputCls} value={form.slug} onChange={set("slug")} disabled={!isNew} data-testid="article-slug-input" /></div>
              <div><label className={labelCls}>Author</label><input className={inputCls} value={form.author} onChange={set("author")} /></div>
              <div className="col-span-2">
                <div className="flex items-center justify-between"><label className={labelCls}>Title</label>
                  <AiAssistButton kind="seo_title" subject={form.title || form.slug} provider="gemini" onGenerated={(t) => setForm({ ...form, title: t.replace(/^["']|["']$/g, "").trim() })} label="Title" />
                </div>
                <input className={inputCls} value={form.title} onChange={set("title")} data-testid="article-title-input" />
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between"><label className={labelCls}>Excerpt</label>
                  <AiAssistButton kind="seo_description" subject={form.title || form.slug} provider="gemini" onGenerated={(t) => setForm({ ...form, excerpt: t })} label="Excerpt" />
                </div>
                <textarea className={`${inputCls} h-16`} value={form.excerpt} onChange={set("excerpt")} />
              </div>
              <div className="col-span-2"><label className={labelCls}>Hero image URL</label><input className={inputCls} value={form.hero_image_url} onChange={set("hero_image_url")} /></div>
              <div className="col-span-2"><label className={labelCls}>Tags (comma-separated)</label><input className={inputCls} value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="umrah, 2026, karachi" /></div>
              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Body (markdown)</label>
                  <div className="flex items-center gap-3">
                    <AiAssistButton kind="article_outline" subject={form.title || form.slug} provider="claude" onGenerated={(t) => setForm({ ...form, body_markdown: (form.body_markdown ? form.body_markdown + "\n\n" : "") + t })} label="Outline" />
                    <AiAssistButton kind="article_body" subject={form.title || form.slug} provider="claude" context={form.excerpt} onGenerated={(t) => setForm({ ...form, body_markdown: t })} label="Draft" />
                  </div>
                </div>
                <textarea className={`${inputCls} h-56 font-mono text-xs`} value={form.body_markdown} onChange={set("body_markdown")} data-testid="article-body-input" />
              </div>
              <div className="col-span-2"><label className={labelCls}>SEO title</label><input className={inputCls} value={form.seo_title} onChange={set("seo_title")} /></div>
              <div className="col-span-2"><label className={labelCls}>SEO description</label><input className={inputCls} value={form.seo_description} onChange={set("seo_description")} /></div>
              <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!form.published} onChange={set("published")} className="accent-[#970C10] w-4 h-4" /> Published
              </label>
            </div>
          )}
          <button onClick={save} data-testid="article-save-btn" className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-2.5 rounded-lg">Save</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
